import { useRouter, type Href } from "expo-router";
import { MAX_INVITES } from "@lezz/shared/constants";
import { Callout } from "@/shared/components/ui/callout";
import { useMyInvites } from "@/features/auth/api/invites";
import { CALLOUTS, type CalloutPlacement } from "../constants/callouts";
import { useCallout } from "../hooks/use-callout";

const ID = "invite_friends" as const;

function describeSlotsLeft(slotsLeft: number) {
  return slotsLeft === 1
    ? "Você ainda tem 1 convite para usar!"
    : `Você ainda tem ${slotsLeft} convites para usar!`;
}

/**
 * Nudges users who still have invites left to send towards "Convidar amigas".
 * Hides itself once every slot has been used, so the impression denominator
 * stays "users who still have an invite to send".
 */
export function InviteCallout({
  placement,
  className,
}: {
  placement: CalloutPlacement;
  className?: string;
}) {
  const router = useRouter();
  const { data: invites, isLoading } = useMyInvites();
  const slotsLeft = MAX_INVITES - (invites?.length ?? 0);
  const { isVisible, dismiss, trackClick } = useCallout(ID, placement, {
    // `data` is undefined until the query resolves, not []. Without the
    // isLoading check, a user with no slots left would flash the callout
    // and capture a false impression before the query settles.
    hidden: isLoading || slotsLeft <= 0,
  });

  if (!isVisible) return null;

  const { icon, title, ctaLabel, href } = CALLOUTS[ID];

  return (
    <Callout
      className={className}
      size="s"
      icon={icon}
      title={title}
      description={describeSlotsLeft(slotsLeft)}
      action={{
        label: ctaLabel,
        onPress: () => {
          trackClick();
          router.push(`${href}?source=${placement}` as Href);
        },
      }}
      onDismiss={dismiss}
    />
  );
}
